import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  UserPlus,
  RefreshCw,
  ChevronLeft,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
} from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import OnboardingDocumentActions from '../common/OnboardingDocumentActions';
import { toast } from 'react-toastify';
import { DOCUMENT_TYPE_OPTIONS, documentDisplayName } from '../../constants/documentTypes';
import {
  displayValue,
  displayTitleCase,
  formatBoolean,
  formatCurrency,
  formatProfileDate,
  normalizeUserProfile,
  getUserIdentifier,
} from '../../utils/userProfile';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export const formatCreated = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const displayOrgName = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '—';
  const key = raw.replace(/\s+/g, '').toLowerCase();
  if (key === 'regalfincorp') return 'Regal Fincorp';
  if (key === 'pfs') return 'PFS';
  return displayTitleCase(raw);
};

const formatDocumentSize = (size) => {
  const num = Number(size);
  if (!Number.isFinite(num) || num <= 0) return 'Size unavailable';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

/** Backend list shapes vary; stats use summary.pending — list often mirrors GET /admin/employees ({ employees }). */
const ARRAY_KEYS = [
  'employees',
  'users',
  'onboardingRequests',
  'requests',
  'items',
  'results',
  'records',
  'data',
];

const firstArrayFromObject = (obj) => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  for (const key of ARRAY_KEYS) {
    if (Array.isArray(obj[key])) return obj[key];
  }
  return null;
};

export const pickListRows = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;

  const top = firstArrayFromObject(d);
  if (top) return top;

  if (d?.data != null) {
    if (Array.isArray(d.data)) return d.data;
    const nested = firstArrayFromObject(d.data);
    if (nested) return nested;
  }

  return [];
};

export const normalizeRow = (item) => {
  const nested = item?.user || item?.employee || {};
  const id = item?._id || item?.id || getUserIdentifier(nested) || getUserIdentifier(item);
  return {
    _id: id,
    name: item?.name ?? nested.name,
    email: item?.email ?? nested.email,
    employeeId: item?.employeeId ?? nested.employeeId,
    tempEmployeeId: item?.tempEmployeeId ?? nested.tempEmployeeId,
    jobTitle: item?.jobTitle ?? nested.jobTitle,
    organization: item?.organization ?? nested.organization,
    createdAt: item?.createdAt ?? item?.created_at ?? nested.createdAt,
    onboardingStatus: item?.onboardingStatus ?? nested.onboardingStatus,
  };
};

const pickDetailProfile = (res) => {
  const d = res?.data;
  if (!d) return null;
  const raw = d.employee ?? d.user ?? d.profile ?? d;
  if (typeof raw !== 'object' || raw === null) return null;
  const normalized = normalizeUserProfile(raw);
  if (!getUserIdentifier(normalized) && !normalized.name && !normalized.email) return null;
  return normalized;
};

const ViewRow = ({ label, children }) => (
  <div className="grid grid-cols-1 gap-1 border-b border-slate-100 py-2 text-sm last:border-0 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] sm:gap-4">
    <dt className="text-slate-500 sm:pt-0.5">{label}</dt>
    <dd className="break-words whitespace-normal font-medium text-slate-900 sm:text-left">{children}</dd>
  </div>
);

const ViewSection = ({ title, children, titleSentenceCase = false }) => (
  <div className="pt-3 first:pt-0">
    <h4
      className={`mb-2 text-xs font-semibold tracking-[0.22em] text-slate-400 ${
        titleSentenceCase ? '' : 'uppercase'
      }`}
    >
      {title}
    </h4>
    <dl className="divide-y divide-slate-50">{children}</dl>
  </div>
);

const hasAnyValue = (obj, fields) =>
  fields.some((f) => {
    const v = obj[f];
    return v !== null && v !== undefined && v !== '' && v !== false;
  });

const ListView = ({ status, onStatusChange, rows, loading, onRefresh }) => (
  <div className="space-y-6">
    <div className="flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-slate-200 bg-gradient-to-r from-white to-slate-50/80 p-5 shadow-sm sm:p-6">
      <div className="max-w-3xl">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Onboarding Requests</h1>
        <p className="mt-1 text-sm leading-6 text-slate-500">Review and approve new employees before they can sign in.</p>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-600 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
      >
        <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>

    <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white/90 p-2 shadow-sm">
      {STATUS_TABS.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onStatusChange(t.value)}
          className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
            status === t.value
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-transparent text-slate-600 hover:bg-slate-50'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>

    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white/90 shadow-sm">
      <div className="overflow-x-auto">
        {loading ? (
          <LoadingSpinner size="lg" className="py-12" />
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400">No requests in this category</div>
        ) : (
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/90">
              <tr>
                {['S.No.', 'Employee Name', 'Email', 'Temp ID', 'Job Title', 'Organization', 'Date Created', ''].map((h) => (
                  <th
                    key={h || 'act'}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, idx) => (
                <tr
                  key={row._id || `${row.email || ''}-${row.employeeId || ''}-${idx}`}
                  className="hover:bg-slate-50/70"
                >
                  <td className="w-14 px-4 py-3 text-sm tabular-nums text-slate-500">{rows.length - idx}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{displayTitleCase(row.name)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{displayValue(row.email)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{displayValue(row.tempEmployeeId)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{displayTitleCase(row.jobTitle)}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{displayOrgName(row.organization)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{formatCreated(row.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    {row._id ? (
                      <Link
                        to={`/superadmin/onboarding-requests/${row._id}`}
                        className="text-sm font-medium text-indigo-600 transition hover:text-indigo-800"
                      >
                        Review
                      </Link>
                    ) : (
                      <span className="text-sm text-slate-400" title="Missing request id from API">
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

const RejectModal = ({ open, reason, onReasonChange, onClose, onSubmit, submitting }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject onboarding</h3>
        <p className="text-sm text-gray-500 mb-4">Optional: add a reason (shown to admins on the employee list).</p>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          rows={4}
          className="w-full border border-gray-300 rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Rejection reason…"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={onSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const DetailView = ({
  requestId,
  onBack,
  onSidebarPendingRefresh,
  /** Full GET path for profile JSON `{ employee }` (default: onboarding-requests) */
  detailApiPath,
  /** When false, hide Approve / Reject (e.g. approved employees directory) */
  showApproveReject = true,
}) => {
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const detailFetchSeq = useRef(0);

  const profileUrl =
    detailApiPath || `/superadmin/onboarding-requests/${requestId}`;

  const load = useCallback(async () => {
    const seq = ++detailFetchSeq.current;
    setLoading(true);
    setDocumentsLoading(true);
    try {
      const res = await api.get(profileUrl);
      if (seq !== detailFetchSeq.current) return;
      setProfile(pickDetailProfile(res));

      try {
        const docsRes = await api.get(`/documents/employee/${requestId}`);
        if (seq !== detailFetchSeq.current) return;
        setDocuments(docsRes.data?.documents || []);
      } catch (docErr) {
        if (seq !== detailFetchSeq.current) return;
        console.error('Document list error:', docErr);
        toast.error(docErr.response?.data?.message || 'Failed to load employee documents');
        setDocuments([]);
      } finally {
        if (seq === detailFetchSeq.current) {
          setDocumentsLoading(false);
        }
      }
    } catch (err) {
      if (seq !== detailFetchSeq.current) return;
      toast.error(err.response?.data?.message || 'Failed to load request');
      setProfile(null);
      setDocuments([]);
      setDocumentsLoading(false);
    } finally {
      if (seq === detailFetchSeq.current) {
        setLoading(false);
      }
    }
  }, [requestId, profileUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const isPending = profile?.onboardingStatus === 'pending';

  const handleApprove = async () => {
    setActing(true);
    try {
      const res = await api.put(`/superadmin/onboarding-requests/${requestId}/approve`);
      const creds = res.data?.credentials;
      toast.success(
        creds
          ? `Employee approved. Login ID ${creds.employeeId}, default password ${creds.defaultPassword}`
          : 'Employee approved and activated',
        { autoClose: 10000 }
      );
      if (onSidebarPendingRefresh) onSidebarPendingRefresh();
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approve failed');
    } finally {
      setActing(false);
    }
  };

  const handleRejectSubmit = async () => {
    setActing(true);
    try {
      const trimmed = rejectReason.trim();
      await api.put(
        `/superadmin/onboarding-requests/${requestId}/reject`,
        trimmed ? { rejectionReason: trimmed } : {}
      );
      toast.success('Employee rejected');
      setRejectOpen(false);
      setRejectReason('');
      if (onSidebarPendingRefresh) onSidebarPendingRefresh();
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reject failed');
    } finally {
      setActing(false);
    }
  };

  const handleDocumentChanged = (updated) => {
    setDocuments((previous) => [updated, ...previous.filter((doc) => doc.documentType !== updated.documentType)]);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          <ChevronLeft className="h-4 w-4 mr-0.5" />
          Back to list
        </button>
        <p className="text-gray-500">Request not found or unavailable.</p>
      </div>
    );
  }

  const onboardingPdf = documents.find((doc) => doc.documentType === 'onboarding_form');

  return (
    <div className="space-y-6">
      {showApproveReject && (
        <RejectModal
          open={rejectOpen}
          reason={rejectReason}
          onReasonChange={setRejectReason}
          onClose={() => !acting && setRejectOpen(false)}
          onSubmit={handleRejectSubmit}
          submitting={acting}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-indigo-600 transition hover:text-indigo-800"
        >
          <ChevronLeft className="h-4 w-4 mr-0.5" />
          Back to list
        </button>
        {showApproveReject && isPending && (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={acting}
              onClick={handleApprove}
              className="inline-flex items-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4 mr-1.5" />
              Approve
            </button>
            <button
              type="button"
              disabled={acting}
              onClick={() => setRejectOpen(true)}
              className="inline-flex items-center rounded-xl border-2 border-red-500 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-50"
            >
              <XCircle className="h-4 w-4 mr-1.5" />
              Reject
            </button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start gap-4 border-b border-slate-100 pb-4">
          <div className="h-14 w-14 flex-shrink-0 rounded-full bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center">
            <UserPlus className="h-7 w-7 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">{displayTitleCase(profile.name)}</h2>
            <p className="truncate text-sm text-slate-500">{displayValue(profile.email)}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {profile.tempEmployeeId && (
                <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-500">
                  Temp: {profile.tempEmployeeId}
                </span>
              )}
              {profile.employeeId && (
                <span className="rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 font-mono text-xs text-indigo-700">
                  ID: {profile.employeeId}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${
                  profile.onboardingStatus === 'pending'
                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                    : profile.onboardingStatus === 'rejected'
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                }`}
              >
                {profile.onboardingStatus === 'pending' && <Clock className="h-3 w-3" />}
                {profile.onboardingStatus || 'approved'}
              </span>
              <span
                className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                  profile.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {profile.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {profile.onboardingRejectionReason && (
              <p className="mt-2 text-sm text-red-600">Rejection reason: {profile.onboardingRejectionReason}</p>
            )}
          </div>
        </div>

        <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-indigo-200 bg-indigo-50 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-indigo-950">Completed Onboarding Form PDF</h3>
            <p className="mt-1 text-sm text-indigo-700">
              {onboardingPdf ? onboardingPdf.originalFileName : 'The completed PDF has not been generated yet.'}
            </p>
          </div>
          {onboardingPdf ? (
            <OnboardingDocumentActions document={onboardingPdf} employeeId={requestId} onChanged={handleDocumentChanged} />
          ) : (
            <span className="text-sm font-medium text-amber-700">PDF unavailable</span>
          )}
        </div>

        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto pr-1 space-y-1">
          <div className="pt-3 first:pt-0">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Uploaded Documents</h4>
            {documentsLoading ? (
              <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                Loading employee documents...
              </div>
            ) : documents.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                No uploaded documents found for this employee.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {documents.map((doc) => (
                  <div
                    key={doc._id}
                    className="flex h-full flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
                        <div className="min-w-0">
                          <p className="break-words text-sm font-medium text-slate-900">
                            {documentDisplayName(doc, DOCUMENT_TYPE_OPTIONS)}
                          </p>
                          <p className="break-words text-xs text-slate-500">{displayValue(doc.originalFileName)}</p>
                          <p className="mt-1 text-xs text-slate-400">
                            {formatDocumentSize(doc.size)}
                            {' • '}
                            Uploaded {formatCreated(doc.uploadedAt || doc.createdAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <OnboardingDocumentActions document={doc} employeeId={requestId} onChanged={handleDocumentChanged} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <ViewSection title="Employment">
            <ViewRow label="Temp ID">{displayValue(profile.tempEmployeeId)}</ViewRow>
            <ViewRow label="Employee ID">{profile.employeeId ? profile.employeeId : <span className="text-gray-400 italic">Assigned on approval</span>}</ViewRow>
            <ViewRow label="Organization">{displayOrgName(profile.organization)}</ViewRow>
            <ViewRow label="Job Title">{displayTitleCase(profile.jobTitle)}</ViewRow>
            <ViewRow label="Join Date">{formatProfileDate(profile.joinDate)}</ViewRow>
            <ViewRow label="Employment Type">{profile.isIntern ? 'Intern' : 'Full-time'}</ViewRow>
            {profile.isIntern && (
              <ViewRow label="Internship">
                {`${profile.internshipDays ?? 90} days · ends ${formatProfileDate(profile.internshipEndDate)}`}
              </ViewRow>
            )}
          </ViewSection>

          <ViewSection title="Personal">
            <ViewRow label="Date of Birth">{formatProfileDate(profile.dateOfBirth)}</ViewRow>
            <ViewRow label="Gender">{displayValue(profile.gender)}</ViewRow>
            <ViewRow label="Marital Status">{displayValue(profile.maritalStatus)}</ViewRow>
            <ViewRow label="Blood Group">{displayValue(profile.bloodGroup)}</ViewRow>
            <ViewRow label="Physically Challenged">{formatBoolean(profile.physicalChallenged)}</ViewRow>
          </ViewSection>

          <ViewSection title="Contact Details" titleSentenceCase>
            <ViewRow label="Mobile Number">{displayValue(profile.mobileNumber)}</ViewRow>
            <ViewRow label="Alternate Mobile Number">{displayValue(profile.alternateNumber)}</ViewRow>
            <ViewRow label="Emergency Name">{displayValue(profile.emergencyName)}</ViewRow>
            <ViewRow label="Emergency Relation">{displayValue(profile.emergencyRelation)}</ViewRow>
            <ViewRow label="Emergency Mobile Number">{displayValue(profile.emergencyContactNumber)}</ViewRow>
          </ViewSection>

          <ViewSection title="Address">
            <ViewRow label="Present Address">{displayValue(profile.presentAddress || profile.address)}</ViewRow>
            <ViewRow label="Permanent Address">{displayValue(profile.permanentAddress)}</ViewRow>
            <ViewRow label="City">{displayValue(profile.city)}</ViewRow>
            <ViewRow label="State">{displayValue(profile.state)}</ViewRow>
            <ViewRow label="Pin Code">{displayValue(profile.pinCode)}</ViewRow>
          </ViewSection>

          <ViewSection title="Documents & Compliance">
            <ViewRow label="PAN Number">{displayValue(profile.panNumber)}</ViewRow>
            <ViewRow label="Aadhaar Number">{displayValue(profile.aadhaarNumber)}</ViewRow>
            <ViewRow label="Passport">{displayValue(profile.passportNumber)}</ViewRow>
            <ViewRow label="PF Details">{displayValue(profile.pfDetails)}</ViewRow>
            <ViewRow label="Medical Insurance">{displayValue(profile.medicalInsurance)}</ViewRow>
          </ViewSection>

          <ViewSection title="Payroll (monthly CTC)">
            <ViewRow label="Basic salary (₹ / month)">{formatCurrency(profile.ctcBasic)}</ViewRow>
            <ViewRow label="HRA (₹ / month)">{formatCurrency(profile.ctcHRA)}</ViewRow>
            <ViewRow label="Special allowance (₹ / month)">{formatCurrency(profile.ctcSpecialAllowance)}</ViewRow>
            <ViewRow label="Travel allowance (₹ / month)">{formatCurrency(profile.ctcTravelAllowance)}</ViewRow>
            <ViewRow label="IT allowance (₹ / month)">{formatCurrency(profile.ctcITAllowance)}</ViewRow>
            <ViewRow label="Total CTC (₹ / month)">{formatCurrency(profile.payScale)}</ViewRow>
          </ViewSection>

          {hasAnyValue(profile, [
            'schoolName',
            'universityName',
            'postGraduationName',
            'highestEducationInstitutionName',
          ]) && (
            <ViewSection title="Education">
              {profile.schoolName && (
                <ViewRow label="School">
                  {profile.schoolName} ({displayValue(profile.schoolYearOfGraduation)})
                </ViewRow>
              )}
              {profile.universityName && (
                <ViewRow label="University">
                  {profile.universityName} ({displayValue(profile.universityYearOfGraduation)})
                </ViewRow>
              )}
              {profile.postGraduationName && (
                <ViewRow label="Post Graduation">
                  {profile.postGraduationName} ({displayValue(profile.postGraduationYearOfGraduation)})
                </ViewRow>
              )}
              {profile.highestEducationInstitutionName && (
                <ViewRow label="Highest Education">
                  {profile.highestEducationInstitutionName} ({displayValue(profile.highestEducationYearOfPassing)})
                </ViewRow>
              )}
            </ViewSection>
          )}

          {hasAnyValue(profile, ['reference1Name', 'reference2Name']) && (
            <div className="pt-3">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">References</h4>
              {[1, 2].map((n) => {
                const name = profile[`reference${n}Name`];
                if (!name) return null;
                return (
                  <div key={n} className="mb-4 last:mb-0">
                    <p className="text-xs font-medium text-gray-600 pb-2">Reference {n}</p>
                    <dl className="divide-y divide-gray-50">
                    <ViewRow label="Name">{displayValue(name)}</ViewRow>
                    <ViewRow label="Relationship">{displayValue(profile[`reference${n}Relationship`])}</ViewRow>
                    <ViewRow label="Mobile">{displayValue(profile[`reference${n}Mobile`])}</ViewRow>
                    <ViewRow label="Emergency Contact">{displayValue(profile[`reference${n}EmergencyContactNumber`])}</ViewRow>
                    <ViewRow label="Address">{displayValue(profile[`reference${n}Address`])}</ViewRow>
                    </dl>
                  </div>
                );
              })}
            </div>
          )}

          {hasAnyValue(profile, ['previousCompanyName', 'totalExperience']) && (
            <ViewSection title="Experience">
              <ViewRow label="Total Experience">
                {profile.totalExperience != null ? `${profile.totalExperience} years` : 'N/A'}
              </ViewRow>
              {profile.previousCompanyName && (
                <>
                  <ViewRow label="Previous Company">{profile.previousCompanyName}</ViewRow>
                  <ViewRow label="Previous Title">{displayValue(profile.previousJobTitle)}</ViewRow>
                  <ViewRow label="Supervisor">{displayValue(profile.previousSupervisorName)}</ViewRow>
                  <ViewRow label="Supervisor Contact">{displayValue(profile.previousSupervisorContact)}</ViewRow>
                  <ViewRow label="Supervisor Email">{displayValue(profile.previousSupervisorEmail)}</ViewRow>
                  <ViewRow label="Duration">
                    {formatProfileDate(profile.previousStartDate)} – {formatProfileDate(profile.previousEndDate)}
                  </ViewRow>
                  <ViewRow label="Reason for Leaving">{displayValue(profile.reasonForLeaving)}</ViewRow>
                  <ViewRow label="Relieving Letter Submitted">{formatBoolean(profile.relievingLetterSubmitted)}</ViewRow>
                </>
              )}
            </ViewSection>
          )}

          <ViewSection title="Acknowledgement">
            <ViewRow label="Acknowledgement Accepted">{formatBoolean(profile.acknowledgementAccepted)}</ViewRow>
            <ViewRow label="Applicant Signature">{displayValue(profile.candidateSignature)}</ViewRow>
            <ViewRow label="Declaration Date">{formatProfileDate(profile.candidateDeclarationDate)}</ViewRow>
          </ViewSection>
        </div>
      </div>
    </div>
  );
};

const SuperAdminOnboardingRequests = ({ onSidebarPendingRefresh }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const listFetchSeq = useRef(0);

  const fetchList = useCallback(async () => {
    const seq = ++listFetchSeq.current;
    setLoading(true);
    try {
      const res = await api.get('/superadmin/onboarding-requests', { params: { status } });
      if (seq !== listFetchSeq.current) return;
      setRows(pickListRows(res).map(normalizeRow));
    } catch (err) {
      if (seq !== listFetchSeq.current) return;
      toast.error(err.response?.data?.message || 'Failed to load onboarding requests');
      setRows([]);
    } finally {
      if (seq === listFetchSeq.current) {
        setLoading(false);
      }
    }
  }, [status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onBack = () => navigate('/superadmin/onboarding-requests');

  if (id) {
    return (
      <DetailView
        requestId={id}
        onBack={onBack}
        onSidebarPendingRefresh={onSidebarPendingRefresh}
      />
    );
  }

  return (
    <ListView
      status={status}
      onStatusChange={setStatus}
      rows={rows}
      loading={loading}
      onRefresh={fetchList}
    />
  );
};

export default SuperAdminOnboardingRequests;
